import { useQuery } from '@tanstack/react-query';

import type { SupportedTimeZone } from '@/constants/date';
import type { AgentsSummaryRequest, AgentsSummaryResponse } from '@/pages/analytics/types';
import { analyticsService } from '@/services/api';
import { naiveDateToUtc } from '@/utils/date';
import { serializeMultiValueParam } from '@/utils/urlParams';

import { analyticsQueryKeys } from './useOrgAnalytics';

// Drop agents missing an id or name — backend placeholder rows we don't want
// surfaced anywhere (KPIs, chips, dropdown, or table).
const dropUnnamedAgents = (res: AgentsSummaryResponse): AgentsSummaryResponse => ({
  ...res,
  agents: res.agents.filter(a => !!a.agent_id && !!a.agent_name),
});

interface UseAgentsSummaryArgs {
  organizationId: string | null;
  programIds?: string[];
  agentIds?: string[];
  callDirections?: string[];
  start?: string;
  end?: string;
  timeZone: SupportedTimeZone;
}

export const useAgentsSummary = ({
  organizationId,
  programIds,
  agentIds,
  callDirections,
  start,
  end,
  timeZone,
}: UseAgentsSummaryArgs) => {
  const params: AgentsSummaryRequest = {
    organization_id: organizationId ?? '',
    program_ids: programIds && programIds.length ? programIds.join(',') : undefined,
    agent_ids: agentIds && agentIds.length ? agentIds.join(',') : undefined,
    call_directions: serializeMultiValueParam(callDirections),
    start: start ? naiveDateToUtc(start, timeZone, 'start') : undefined,
    end: end ? naiveDateToUtc(end, timeZone, 'end') : undefined,
  };
  const { organization_id, ...queryParams } = params;

  return useQuery({
    queryKey: [...analyticsQueryKeys.all, 'agents', 'summary', params] as const,
    queryFn: async () =>
      (await analyticsService.getAgentsSummary(
        { organization_id },
        { params: queryParams },
      )) as AgentsSummaryResponse,
    select: dropUnnamedAgents,
    enabled: !!organizationId,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
};
